import {
    type UIEvent,
    useCallback,
    useEffect,
    useRef,
    ReactElement,
} from 'react'
import {
    MaterialReactTable,
    useMaterialReactTable,
    type MRT_ColumnDef,
    type MRT_ColumnFiltersState,
    type MRT_SortingState,
    type MRT_RowVirtualizer,
    type MRT_RowSelectionState,
    type MRT_RowData,
    type MRT_Row,
    type MRT_Cell,
    type MRT_TableOptions,
} from 'material-react-table'
import { Box, Typography } from '@mui/material'
import {
    FetchNextPageOptions,
    InfiniteData,
    InfiniteQueryObserverResult,
} from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import TruncatedText from './TruncatedText'
import { getPrimitiveDisplayValue } from '../../utils/StringUtils'

interface IMRTTable<DataType extends MRT_RowData, ResponseType> {
    columns: MRT_ColumnDef<DataType>[]
    sorting: MRT_SortingState
    globalFilter: string | undefined
    columnFilters: MRT_ColumnFiltersState
    setRowSelection?: React.Dispatch<
        React.SetStateAction<MRT_RowSelectionState>
    >
    rowSelection?: MRT_RowSelectionState
    setColumnFilters: React.Dispatch<
        React.SetStateAction<MRT_ColumnFiltersState>
    >
    setGlobalFilter: React.Dispatch<React.SetStateAction<string | undefined>>
    setSorting: React.Dispatch<React.SetStateAction<MRT_SortingState>>
    data: DataType[]
    totalDBRowCount: number
    fetchNextPage: (
        options?: FetchNextPageOptions | undefined,
    ) => Promise<
        InfiniteQueryObserverResult<InfiniteData<ResponseType, unknown>, Error>
    >
    isError: boolean
    isFetching: boolean
    isLoading: boolean
    enableBottomToolbar: boolean
    isCurvedBottomToolbar: boolean
    enableRowSelection: boolean
    bottomToolbarPadding?: string
    enableSorting?: boolean
    maxHeight?: string
    enableExpanding?: boolean
    subRows?: string
    renderDetailPanel?: (params: {
        row: MRT_Row<DataType>
    }) => ReactElement | null
    muiDetailPanelProps?: any
    layoutMode?: 'semantic' | 'grid' | 'grid-no-grow'
    initialState?: Partial<MRT_TableOptions<DataType>['initialState']>
    displayColumnDefOptions?: MRT_TableOptions<DataType>['displayColumnDefOptions']
    columnMaxWidth?: number
}

const MRTTable = <DataType extends MRT_RowData, ResponseType>(
    props: IMRTTable<DataType, ResponseType>,
): ReactElement => {
    const {
        columns,
        sorting,
        globalFilter,
        columnFilters,
        setRowSelection = () => {},
        rowSelection = {},
        setColumnFilters,
        setGlobalFilter,
        setSorting,
        data,
        totalDBRowCount,
        fetchNextPage,
        isError,
        isFetching,
        isLoading,
        enableBottomToolbar,
        isCurvedBottomToolbar,
        bottomToolbarPadding = '10px',
        enableRowSelection,
        enableSorting,
        maxHeight = '500px',
        enableExpanding,
        subRows,
        renderDetailPanel,
        initialState,
        displayColumnDefOptions,
        muiDetailPanelProps,
        layoutMode = 'semantic',
        columnMaxWidth = 9001,
    } = props
    const { t } = useTranslation()

    const tableContainerRef = useRef<HTMLDivElement>(null) // we can get access to the underlying TableContainer element and react to its scroll events
    const rowVirtualizerInstanceRef = useRef<MRT_RowVirtualizer>(null) // we can get access to the underlying Virtualizer instance and call its scrollToIndex method

    const totalFetched = data.length

    // called on scroll and possibly on mount to fetch more data as the user scrolls and reaches bottom of table
    const fetchMoreOnBottomReached = useCallback(
        (containerRefElement?: HTMLDivElement | null) => {
            if (containerRefElement) {
                const { scrollHeight, scrollTop, clientHeight } =
                    containerRefElement
                // once the user has scrolled within 400px of the bottom of the table, fetch more data if we can
                if (
                    scrollHeight - scrollTop - clientHeight < 400 &&
                    !isFetching &&
                    totalFetched < totalDBRowCount
                ) {
                    fetchNextPage()
                }
            }
        },
        [fetchNextPage, isFetching, totalFetched, totalDBRowCount],
    )

    // scroll to top of table when sorting or filters change
    useEffect(() => {
        // scroll to the top of the table when the sorting changes
        try {
            rowVirtualizerInstanceRef.current?.scrollToIndex?.(0)
        } catch (error) {
            console.error(error)
        }
    }, [sorting, columnFilters, globalFilter])

    // a check on mount to see if the table is already scrolled to the bottom and immediately needs to fetch more data
    useEffect(() => {
        fetchMoreOnBottomReached(tableContainerRef.current)
    }, [fetchMoreOnBottomReached])

    let columnOrder = columns.map((c) => c.id || (c.accessorKey as string))

    if (enableExpanding || renderDetailPanel) {
        columnOrder = ['mrt-row-expand', ...columnOrder]
    }

    if (enableRowSelection) {
        columnOrder = ['mrt-row-select', ...columnOrder]
    }

    const table = useMaterialReactTable({
        columns,
        data,
        renderEmptyRowsFallback: () => (
            <Typography variant="body1" sx={{ p: 2, textAlign: 'center' }}>
                {t('noRecordsToDisplay')}
            </Typography>
        ),
        enableBatchRowSelection: true,
        enableMultiRowSelection: true,

        displayColumnDefOptions: displayColumnDefOptions || {
            'mrt-row-select': { header: '', size: 10 },
            'mrt-row-expand': {
                header: '',
                size: 70,
            },
        },
        enablePagination: false,
        enableRowSelection,
        enableSelectAll: false,
        enableColumnPinning: true,
        enableStickyHeader: true,
        manualFiltering: true,
        manualSorting: true,
        enableColumnActions: false,
        enableSorting,
        layoutMode,
        muiTableHeadProps: {
            sx: {
                th: {
                    // borderBottom: `1px solid ${theme.palette.primary.main}`,
                    '&:first-of-type': {
                        paddingLeft: enableRowSelection ? '0px' : '15px',
                    },
                },
            },
        },
        muiTablePaperProps: {
            sx: {
                borderBottomLeftRadius: isCurvedBottomToolbar ? '20px' : '0px',
                borderBottomRightRadius: isCurvedBottomToolbar ? '20px' : '0px',
            },
        },
        muiTableBodyRowProps: ({ row }) => ({
            // add onClick to row to select upon clicking anywhere in the row
            onClick: row.getToggleSelectedHandler(),
            sx: {
                cursor: 'pointer',
                td: {
                    backgroundColor: row.depth > 0 ? '#f9f9f9' : '',
                },
            },
        }),

        muiTableBodyCellProps: ({ cell }: { cell: MRT_Cell<DataType> }) => {
            const isDetailPanelCell =
                Boolean(cell.getIsPlaceholder?.()) &&
                Boolean(cell.row.getIsExpanded?.())

            return {
                sx: {
                    backgroundColor: '#E9F6FF',
                    borderBottom: '3px solid #FFFFFF',
                    borderTop: '3px solid #FFFFFF',
                    fontSize: '14px',
                    '&:first-of-type': {
                        borderLeft: '7px solid #FFFFFF',
                        paddingLeft:
                            isDetailPanelCell || enableRowSelection
                                ? '0px'
                                : '11px',
                    },
                    ...(isDetailPanelCell && {
                        '&.MuiTableCell-root.MuiTableCell-body.MuiTableCell-sizeMedium.Mui-TableBodyCell-DetailPanel':
                            {
                                paddingLeft: '0px',
                                paddingRight: '0px',
                            },
                    }),
                    '&:last-child': {
                        borderRight: '6px solid #FFFFFF',
                    },
                },
            }
        },
        muiTableContainerProps: {
            ref: tableContainerRef,
            sx: {
                flex: '1 1 0',
                maxHeight,
            },
            onScroll: (event: UIEvent<HTMLDivElement>) =>
                fetchMoreOnBottomReached(event.target as HTMLDivElement), // add an event listener to the table container element
        },
        muiTableHeadCellProps: {
            sx: { fontSize: '14px' },
        },
        muiToolbarAlertBannerProps: isError
            ? {
                  color: 'error',
                  children: t('errorLoadingData'),
              }
            : undefined,
        muiBottomToolbarProps: {
            sx: {
                minHeight: '0px',
            },
        },
        muiDetailPanelProps: muiDetailPanelProps || {},
        icons: {
            ArrowDownwardIcon: (props: any) => {
                return (
                    <Box
                        {...props}
                        sx={{
                            fontSize: '14px !important',
                            height: '16px',
                            display: 'flex',
                            marginBottom: '5px',
                            '&.MuiTableSortLabel-iconDirectionAsc': {
                                alignItems: 'flex-end !important',
                            },
                            '&.MuiTableSortLabel-iconDirectionDesc': {
                                alignItems: 'flex-start !important',
                            },
                        }}
                    >
                        <Box>▼</Box>
                    </Box>
                )
            },
        },
        onColumnFiltersChange: setColumnFilters,
        onGlobalFilterChange: setGlobalFilter,
        onSortingChange: setSorting,
        onRowSelectionChange: setRowSelection,
        renderBottomToolbarCustomActions: () => (
            <Typography
                variant="h6"
                sx={{ padding: bottomToolbarPadding, paddingLeft: '7px' }}
            >
                {t('displayingRecords', { totalFetched, totalDBRowCount })}
            </Typography>
        ),
        // muiExpandButtonProps: {
        //     sx: { color: '#000' },
        // },
        getSubRows: (originalRow) => originalRow[subRows || 'subRows'],
        state: {
            columnOrder,
            columnFilters,
            globalFilter,
            isLoading,
            showAlertBanner: isError,
            showProgressBars: isFetching,
            sorting,
            rowSelection,
            density: 'compact',
            ...initialState, // Spread new initialState here
        },
        rowVirtualizerInstanceRef,
        rowVirtualizerOptions: { overscan: 4 },
        enableTopToolbar: false,
        enableBottomToolbar,
        defaultColumn: {
            minSize: 50, //allow columns to get smaller than default
            maxSize: columnMaxWidth, //allow columns to get larger than default
            size: 260, //make columns wider by default
            Cell: ({ cell }) => {
                const displayValue = getPrimitiveDisplayValue(cell.getValue())

                return (
                    <Box sx={{ width: '100%' }}>
                        <TruncatedText text={displayValue} />
                    </Box>
                )
            },
        },
        enableExpanding,
        enableExpandAll: false,
        positionExpandColumn: 'first',
        renderDetailPanel,
    })

    return <MaterialReactTable table={table} />
}

export default MRTTable
